import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { NativeTabBar } from './NativeTabBar';
import { isNative } from '../native';
import axios from 'axios';
import { 
  Trophy, 
  Users, 
  Calendar, 
  Sun, 
  Moon, 
  Menu, 
  X, 
  User, 
  LogOut,
  Home,
  LayoutDashboard,
  Flag,
  UserPlus,
  ArrowLeftRight,
  Compass,
  Map,
  Heart,
  Bell,
  Search,
  Shield,
  BarChart3,
  GitCompare,
  Download,
  Archive,
  ClipboardList,
  Medal,
  MessageSquare,
  Palette,
  Settings,
  UserCheck,
  ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const native = isNative();

  // Primary destinations get a permanent slot in the desktop nav; everything
  // else lives one tap away in the "More" menu so the header doesn't have to
  // cram 13 items into a single row.
  const primaryNavItems = [
    { path: '/', label: t('nav.home'), icon: Home },
    { path: '/discover', label: 'Discover', icon: Compass },
    { path: '/tournaments', label: t('nav.tournaments'), icon: Trophy },
    { path: '/teams', label: t('nav.teams'), icon: Users },
    { path: '/leagues', label: t('nav.leagues'), icon: Calendar },
  ];

  const exploreItems = [
    { path: '/search', label: t('common.search'), icon: Search },
    { path: '/map', label: 'Map', icon: Map },
    { path: '/free-agents', label: 'Free Agents', icon: UserPlus },
    { path: '/referee', label: t('game.referee'), icon: UserCheck },
  ];

  const statsItems = [
    { path: '/performance', label: t('player.stats'), icon: BarChart3 },
    { path: '/compare', label: 'Compare', icon: GitCompare },
    { path: '/leaderboard/players', label: t('nav.leaderboard'), icon: Medal },
    { path: '/archives', label: 'Archives', icon: Archive },
  ];

  const moreItems = [...exploreItems, ...statsItems];
  const navItems = [...primaryNavItems, ...moreItems];
  const isMoreActive = moreItems.some(item => location.pathname === item.path);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    }
    
    // PWA Install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [user]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="nav-sticky" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group" data-testid="logo-link">
              <img
                src="/logo192.png"
                alt="Road to Glory"
                className="h-10 w-10 rounded-xl object-cover ring-1 ring-border shadow-sm group-hover:ring-primary/50 transition-all"
              />
              <span className="brand-wordmark hidden sm:block text-2xl leading-none uppercase whitespace-nowrap">
                Road to <span className="text-primary">Glory</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {primaryNavItems.map(item => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={location.pathname === item.path ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-2"
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isMoreActive ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-1.5"
                    data-testid="nav-more"
                  >
                    More
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Explore</DropdownMenuLabel>
                  {exploreItems.map(item => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link
                        to={item.path}
                        className="flex items-center gap-2"
                        data-testid={`nav-more-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Stats & History</DropdownMenuLabel>
                  {statsItems.map(item => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link
                        to={item.path}
                        className="flex items-center gap-2"
                        data-testid={`nav-more-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* Notifications Bell - Only for logged in users */}
              {user && (
                <Link to="/notifications">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative tap-target"
                    data-testid="notifications-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <Badge 
                        className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </Link>
              )}

              {/* Favorites - Only for logged in users */}
              {user && (
                <Link to="/favorites">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tap-target"
                    data-testid="favorites-link"
                  >
                    <Heart className="w-5 h-5" />
                  </Button>
                </Link>
              )}

              {/* Install App Button */}
              {canInstall && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInstallClick}
                  className="gap-2 hidden sm:flex"
                  data-testid="install-app-btn"
                >
                  <Download className="w-4 h-4" />
                  Install
                </Button>
              )}

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="tap-target"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User Menu / Auth */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="hidden sm:block max-w-[100px] truncate">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/following/teams" className="flex items-center gap-2" data-testid="followed-teams-link">
                        <Heart className="w-4 h-4 text-red-500" />
                        Followed Teams
                      </Link>
                    </DropdownMenuItem>
                    {(user?.role === 'organizer' || user?.role === 'admin') && (
                      <DropdownMenuItem asChild>
                        <Link to="/templates" className="flex items-center gap-2" data-testid="templates-link">
                          <ClipboardList className="w-4 h-4" />
                          My Templates
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user?.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                          <Shield className="w-4 h-4 text-red-500" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/settings/sms" className="flex items-center gap-2" data-testid="sms-settings-link">
                        <MessageSquare className="w-4 h-4" />
                        SMS Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings/branding" className="flex items-center gap-2" data-testid="branding-settings-link">
                        <Palette className="w-4 h-4" />
                        Branding
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-destructive"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" data-testid="login-link">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="btn-primary" data-testid="register-link">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden tap-target"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-background/95 backdrop-blur" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-1">
              {navItems.map(item => (
                <Link 
                  key={item.path} 
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant={location.pathname === item.path ? "secondary" : "ghost"}
                    className="w-full justify-start gap-2"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className={`min-h-[calc(100vh-4rem)] ${native ? 'pb-20' : ''}`}>
        {children}
      </main>

      {/* Footer - web only; a marketing footer reads as "website chrome" in the native app */}
      {!native && (
        <footer className="border-t py-8 mt-16" data-testid="footer">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <img
                  src="/logo192.png"
                  alt="Road to Glory"
                  className="h-8 w-8 rounded-lg object-cover"
                />
                <span className="brand-wordmark">Road to Glory Flag Football</span>
              </div>
              <p className="text-sm text-muted-foreground">
                AI-Powered Flag Football Tournament & League Management
              </p>
            </div>
          </div>
        </footer>
      )}

      {/* Native primary navigation */}
      {native && <NativeTabBar onMenuPress={() => setMobileMenuOpen(true)} />}
    </div>
  );
};
